import React from 'react';
import { Eye, EyeOff, X, Scissors, Crop } from 'lucide-react';
import ParameterSlider from './ParameterSlider';
import ColorPicker from './ColorPicker';
import FileUpload from './FileUpload';
import { Layer, LayerParameters, UserPattern } from '../types';
import PatternGallery from './PatternGallery';

interface LayerPanelProps {
  layer: Layer;
  onParameterChange: (
    layerId: string,
    paramName: keyof LayerParameters,
    value: number | string
  ) => void;
  onSvgPatternChange: (
    layerId: string,
    svgPattern: string,
    visible: boolean
  ) => void;
  onVisibilityChange: (layerId: string, visible: boolean) => void;
  onDeleteLayer: (layerId: string) => void;
  onClearMask: (layerId: string) => void;
  onSelectForMask: (layerId: string) => void;
  userPatterns?: UserPattern[];
  isLoadingPatterns?: boolean;
  onPatternDelete?: (patternId: string) => Promise<boolean>;
  onPatternUpload?: (file: File) => Promise<boolean>;
}

const LayerPanel: React.FC<LayerPanelProps> = ({
  layer,
  onParameterChange,
  onSvgPatternChange,
  onVisibilityChange,
  onDeleteLayer,
  onClearMask,
  onSelectForMask,
  userPatterns = [],
  isLoadingPatterns = false,
  onPatternDelete,
  onPatternUpload,
}) => {
  const handleSvgUpload = async (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        onSvgPatternChange(layer.id, e.target.result as string, true);
      }
    };
    reader.readAsText(file);
  };

  const handlePatternSelect = async (svgUrl: string) => {
    try {
      const response = await fetch(svgUrl);
      if (response.ok) {
        let svgContent = await response.text();

        if (
          svgContent.trim().startsWith('{') &&
          svgContent.includes('svgContent')
        ) {
          try {
            const jsonData = JSON.parse(svgContent);
            if (jsonData.svgContent) {
              svgContent = jsonData.svgContent;
            }
          } catch (e) {
            console.error('JSON解析エラー:', e);
          }
        }

        onSvgPatternChange(layer.id, svgContent, true);
      } else {
        console.error('パターンの取得に失敗しました:', response.statusText);
      }
    } catch (error) {
      console.error('パターン選択エラー:', error);
    }
  };

  const handlePatternUpload = async (file: File): Promise<boolean> => {
    if (!onPatternUpload) return false;
    return await onPatternUpload(file);
  };

  const galleryPatterns = userPatterns.map((p) => ({
    patternId: p.patternId,
    cloudFrontUrl: p.cloudFrontUrl || '', // undefinedの場合は空文字列を設定
    createdAt: p.createdAt,
  }));

  return (
    <div className="border p-4 mb-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">
          {layer.displayName || `Layer ${layer.id}`}
        </h3>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => onVisibilityChange(layer.id, !layer.visible)}
            className={`p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 ${
              layer.visible ? 'text-blue-500' : 'text-gray-400'
            }`}
            title={layer.visible ? 'Hide Layer' : 'Show Layer'}
          >
            {layer.visible ? <Eye size={20} /> : <EyeOff size={20} />}
          </button>
          {layer.clipMask ? (
            <button
              onClick={() => onClearMask(layer.id)}
              className="p-2 rounded-full hover:bg-gray-100 hover:text-orange-500 transition-colors duration-200 text-gray-400"
              title="Clear Mask"
            >
              <Scissors size={20} />
            </button>
          ) : (
            <button
              onClick={() => onSelectForMask(layer.id)}
              className="p-2 rounded-full hover:bg-gray-100 hover:text-blue-500 transition-colors duration-200 text-gray-400"
              title="Add Mask"
            >
              <Crop size={20} />
            </button>
          )}
          <button
            onClick={() => onDeleteLayer(layer.id)}
            className="p-2 rounded-full hover:bg-gray-100 hover:text-red-500 transition-colors duration-200 text-gray-400"
            title="Delete Layer"
            aria-label={`Delete ${layer.displayName || `Layer ${layer.id}`}`}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* クラウドのパターンセクションのデザイン調整 */}
      <div className="mb-4">
        <div className="mb-2">
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Select Cloud SVG Pattern
          </span>
          <div className="border-0 bg-white p-2">
            <PatternGallery
              patterns={galleryPatterns}
              onSelect={handlePatternSelect}
              isLoading={isLoadingPatterns}
              onDelete={onPatternDelete}
              onUpload={handlePatternUpload}
            />
          </div>
        </div>
      </div>

      <FileUpload
        onFileSelect={handleSvgUpload}
        accept=".svg"
        id={`svg-upload-${layer.id}`}
        label="Select SVG Pattern"
      />

      <ParameterSlider
        name="Size"
        min={20}
        max={100}
        value={layer.parameters.size}
        onChange={(value) => onParameterChange(layer.id, 'size', value)}
      />
      <ParameterSlider
        name="Gap X"
        min={0}
        max={layer.parameters.size - 10}
        value={layer.parameters.gapX}
        onChange={(value) => onParameterChange(layer.id, 'gapX', value)}
      />
      <ParameterSlider
        name="Gap Y"
        min={0}
        max={layer.parameters.size - 10}
        value={layer.parameters.gapY}
        onChange={(value) => onParameterChange(layer.id, 'gapY', value)}
      />
      <ParameterSlider
        name="Angle"
        min={-180}
        max={180}
        value={layer.parameters.angle}
        onChange={(value) => onParameterChange(layer.id, 'angle', value)}
      />
      <ParameterSlider
        name="Angle View"
        min={-180}
        max={180}
        value={layer.parameters.angleView}
        onChange={(value) => onParameterChange(layer.id, 'angleView', value)}
      />
      <div className="flex items-center mt-2">
        <span className="mr-2">Color:</span>
        <ColorPicker
          color={layer.parameters.color}
          onChange={(color) => onParameterChange(layer.id, 'color', color)}
        />
      </div>
    </div>
  );
};

export default LayerPanel;
